// =============================================================
// DashboardPage.tsx — Phase 7 Step 1: Real Dashboard Activation
// Replaces all demo data with live backend-driven SEO, Google,
// Website Audit, and Automation monitoring telemetry.
// =============================================================

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  businessApi,
  googleApi,
  intelligenceApi,
  automationApi,
  Business,
  WebsiteAudit,
  GoogleStatusResponse,
  SearchConsoleSummaryDto,
  GoogleBusinessSummaryDto,
  OpportunityEngineSummaryDto,
  AutomationSummaryDto,
  AutomationExecutionDto,
} from '../lib/api';

const STORAGE_KEY_SELECTED_BIZ = 'active_dashboard_business_id';

function formatWorkflowName(key: string): string {
  switch (key) {
    case 'website_audit':
      return 'Website Audit';
    case 'search_console_sync':
      return 'Search Console Sync';
    case 'google_business_profile_sync':
      return 'Google Business Profile Sync';
    case 'opportunity_generation':
      return 'Opportunity Generation';
    case 'ai_opportunity_analysis':
      return 'AI Opportunity Analysis';
    case 'weekly_seo_summary':
      return 'Weekly SEO Summary';
    default:
      return key
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
}

function formatTriggerType(type: string): string {
  switch (type) {
    case 'SCHEDULED':
      return 'Scheduled (Cron)';
    case 'MANUAL':
      return 'Manual';
    case 'SYSTEM':
      return 'System Event';
    case 'WEBHOOK':
      return 'Webhook';
    default:
      return type;
  }
}

function formatRelativeTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  } catch {
    return dateString;
  }
}

export default function DashboardPage() {
  const { user, organization } = useAuth();
  const navigate = useNavigate();

  // Business state
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | null>(null);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);

  // Dashboard data states (resiliently isolated)
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  // Widget states
  const [latestAudit, setLatestAudit] = useState<WebsiteAudit | null>(null);
  const [auditErrorsCount, setAuditErrorsCount] = useState<number>(0);
  const [auditWarningsCount, setAuditWarningsCount] = useState<number>(0);

  const [intelligenceSummary, setIntelligenceSummary] = useState<OpportunityEngineSummaryDto | null>(null);

  const [googleStatus, setGoogleStatus] = useState<GoogleStatusResponse | null>(null);
  const [gscSummary, setGscSummary] = useState<SearchConsoleSummaryDto | null>(null);
  const [gbpSummary, setGbpSummary] = useState<GoogleBusinessSummaryDto | null>(null);

  const [automationSummary, setAutomationSummary] = useState<AutomationSummaryDto | null>(null);
  const [recentExecutions, setRecentExecutions] = useState<AutomationExecutionDto[]>([]);

  // 1. Load organization businesses
  useEffect(() => {
    let mounted = true;
    setLoadingBusinesses(true);

    businessApi
      .list()
      .then((data) => {
        if (!mounted) return;
        setBusinesses(data);

        if (data.length > 0) {
          const stored = localStorage.getItem(STORAGE_KEY_SELECTED_BIZ);
          const matched = stored ? data.find((b) => b.id === Number(stored)) : null;
          setSelectedBusinessId(matched ? matched.id : data[0].id);
        } else {
          setSelectedBusinessId(null);
        }
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        const msg = err instanceof Error ? err.message : 'Failed to load businesses';
        setDashboardError(msg);
      })
      .finally(() => {
        if (mounted) setLoadingBusinesses(false);
      });

    return () => {
      mounted = false;
    };
  }, [organization?.id]);

  // Synchronize with external active business changes from TopBar or Sidebar
  useEffect(() => {
    function handleBizChange(e: Event) {
      const customEvent = e as CustomEvent<number>;
      if (customEvent.detail) {
        setSelectedBusinessId(customEvent.detail);
        return;
      }
      const saved = localStorage.getItem(STORAGE_KEY_SELECTED_BIZ);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) setSelectedBusinessId(parsed);
      }
    }

    window.addEventListener('active-business-change', handleBizChange);
    window.addEventListener('storage', handleBizChange);
    return () => {
      window.removeEventListener('active-business-change', handleBizChange);
      window.removeEventListener('storage', handleBizChange);
    };
  }, []);

  // 2. Fetch real dashboard telemetry for selected business
  const fetchDashboardData = useCallback(
    async (bizId: number, isManualRefresh = false) => {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setDashboardLoading(true);
      }
      setDashboardError(null);

      try {
        // Parallel resilient execution using Promise.allSettled
        const [
          auditsResult,
          intelligenceResult,
          googleStatusResult,
          automationSummaryResult,
          executionsResult,
        ] = await Promise.allSettled([
          businessApi.listAudits(bizId),
          intelligenceApi.getSummary(bizId),
          googleApi.getStatus(bizId),
          automationApi.getSummary(bizId),
          automationApi.listExecutions({ businessId: bizId, limit: 6 }),
        ]);

        // Process Website Audit
        if (auditsResult.status === 'fulfilled' && auditsResult.value && auditsResult.value.length > 0) {
          const topAudit = auditsResult.value[0];
          setLatestAudit(topAudit);

          if (topAudit.status === 'COMPLETED') {
            try {
              const findings = await businessApi.getAuditFindings(bizId, topAudit.id);
              setAuditErrorsCount(findings.filter((f) => f.severity === 'ERROR').length);
              setAuditWarningsCount(findings.filter((f) => f.severity === 'WARNING').length);
            } catch {
              setAuditErrorsCount(0);
              setAuditWarningsCount(0);
            }
          } else {
            setAuditErrorsCount(0);
            setAuditWarningsCount(0);
          }
        } else {
          setLatestAudit(null);
          setAuditErrorsCount(0);
          setAuditWarningsCount(0);
        }

        // Process SEO Intelligence
        if (intelligenceResult.status === 'fulfilled' && intelligenceResult.value) {
          setIntelligenceSummary(intelligenceResult.value);
        } else {
          setIntelligenceSummary(null);
        }

        // Process Google Status & dependent metrics
        if (googleStatusResult.status === 'fulfilled' && googleStatusResult.value) {
          const gStatus = googleStatusResult.value;
          setGoogleStatus(gStatus);

          if (gStatus.connected) {
            const [gscRes, gbpRes] = await Promise.allSettled([
              googleApi.getSearchConsoleMetrics(bizId),
              googleApi.getGoogleBusinessMetrics(bizId),
            ]);

            setGscSummary(gscRes.status === 'fulfilled' ? gscRes.value : null);
            setGbpSummary(gbpRes.status === 'fulfilled' ? gbpRes.value : null);
          } else {
            setGscSummary(null);
            setGbpSummary(null);
          }
        } else {
          setGoogleStatus(null);
          setGscSummary(null);
          setGbpSummary(null);
        }

        // Process Automation Summary
        if (automationSummaryResult.status === 'fulfilled' && automationSummaryResult.value) {
          setAutomationSummary(automationSummaryResult.value);
        } else {
          setAutomationSummary(null);
        }

        // Process Recent Executions
        if (executionsResult.status === 'fulfilled' && executionsResult.value?.items) {
          setRecentExecutions(executionsResult.value.items);
        } else {
          setRecentExecutions([]);
        }
      } catch (err: unknown) {
        console.error('Failed to load dashboard data:', err);
        const msg = err instanceof Error ? err.message : 'Failed to fetch dashboard metrics';
        setDashboardError(msg);
      } finally {
        setDashboardLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  // Trigger load when selectedBusinessId changes
  useEffect(() => {
    if (selectedBusinessId) {
      localStorage.setItem(STORAGE_KEY_SELECTED_BIZ, String(selectedBusinessId));
      fetchDashboardData(selectedBusinessId);
    }
  }, [selectedBusinessId, fetchDashboardData]);

  // Handle business selection change
  function handleBusinessChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newId = Number(e.target.value);
    setSelectedBusinessId(newId);
    localStorage.setItem(STORAGE_KEY_SELECTED_BIZ, String(newId));
    window.dispatchEvent(new CustomEvent('active-business-change', { detail: newId }));
  }

  // Handle manual refresh
  function handleRefresh() {
    if (selectedBusinessId && !refreshing) {
      fetchDashboardData(selectedBusinessId, true);
    }
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const selectedBusiness = businesses.find((b) => b.id === selectedBusinessId) ?? null;

  // Render initial loading state
  if (loadingBusinesses) {
    return (
      <DashboardLayout title="Dashboard" subtitle="Loading organization data...">
        <div className="card" style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>⚡</div>
          <p>Loading your businesses and SEO telemetry...</p>
        </div>
      </DashboardLayout>
    );
  }

  // Render no businesses empty state
  if (businesses.length === 0) {
    return (
      <DashboardLayout
        title="Dashboard"
        subtitle={`${organization?.name ?? 'Your Organization'} · Overview`}
      >
        <div className="page-header">
          <div className="page-header-text">
            <h1>{greeting}, {user?.firstName} 👋</h1>
            <p>Welcome to Local SEO AI.</p>
          </div>
        </div>

        <div className="card" style={{ padding: '48px 24px', textAlign: 'center', maxWidth: 640, margin: '20px auto' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🏢</div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
            No Businesses Registered Yet
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 24 }}>
            Create your first business profile to begin tracking technical audits, Google Search Console queries,
            Google Maps visibility, and AI-driven SEO opportunities.
          </p>
          <Link to="/businesses/new" className="btn btn-primary btn-lg">
            + Add Your First Business
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Dashboard"
      subtitle={`${organization?.name ?? 'Your Organization'} · Real Telemetry`}
    >
      {/* Top Header Row with Greeting & Business Selector + Refresh */}
      <div
        className="page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div className="page-header-text">
          <h1>{greeting}, {user?.firstName} 👋</h1>
          <p>Real-time technical SEO health, search performance, and automation operations.</p>
        </div>

        {/* Business Selector & Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="dashboard-biz-select" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>
              Business:
            </label>
            <select
              id="dashboard-biz-select"
              className="form-input"
              value={selectedBusinessId ?? ''}
              onChange={handleBusinessChange}
              style={{ minWidth: 200, padding: '7px 12px', fontSize: '0.88rem' }}
              disabled={refreshing || dashboardLoading}
            >
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.industry ? `(${b.industry})` : ''}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleRefresh}
            disabled={refreshing || dashboardLoading}
            title="Refetch real metrics from backend"
          >
            {refreshing ? '↻ Refreshing...' : '↻ Refresh'}
          </button>

          {selectedBusinessId && (
            <Link
              to={`/businesses/${selectedBusinessId}/dashboard`}
              className="btn btn-primary btn-sm"
              title="Open full business profile & settings"
            >
              Business Hub →
            </Link>
          )}
        </div>
      </div>

      {/* Global Error Banner if initial fetch fails */}
      {dashboardError && (
        <div className="alert alert-error" style={{ marginBottom: 24 }}>
          <span>⚠️</span>
          <div style={{ flex: 1 }}>
            <strong>Dashboard Notice:</strong> {dashboardError}
          </div>
          <button className="btn btn-sm btn-secondary" onClick={handleRefresh}>
            Retry
          </button>
        </div>
      )}

      {/* SECTION: Hero Cards (SEO Score + Quick Actions) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 1fr) minmax(360px, 2fr)',
          gap: 20,
          marginBottom: 24,
        }}
      >
        {/* Real SEO Score Hero Card */}
        <div className="score-card">
          <div className="score-card-label">Website SEO Score</div>
          <div className="score-card-value">
            {latestAudit?.overallScore !== null && latestAudit?.overallScore !== undefined ? (
              <>
                {latestAudit.overallScore}
                <span>/100</span>
              </>
            ) : (
              <span style={{ fontSize: '1.8rem', fontWeight: '700', color: '#ef4444' }}>⚠️ Failed</span>
            )}
          </div>
          <div className="score-card-desc">
            {latestAudit ? (
              latestAudit.status === 'COMPLETED' ? (
                <>
                  Deterministic score from crawl of {latestAudit.pagesCrawled} pages ({auditErrorsCount} errors)
                </>
              ) : latestAudit.status === 'FAILED' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                  <span>Audit failed.</span>
                  <Link to={`/businesses/${selectedBusinessId}/seo`} className="btn btn-primary btn-sm">Retry Audit</Link>
                </div>
              ) : (
                <>Audit in progress ({latestAudit.status.toLowerCase()}...)</>
              )
            ) : (
              <>Score unavailable · Run a website audit to calculate your technical score</>
            )}
          </div>
          {latestAudit && latestAudit.overallScore !== null && (
            <div
              style={{
                display: 'inline-block',
                marginTop: 12,
                background: 'rgba(34, 197, 94, 0.2)',
                color: '#86efac',
                fontSize: '0.68rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '9999px',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                border: '1px solid rgba(134, 239, 172, 0.3)',
              }}
            >
              Verified Crawl Engine
            </div>
          )}
        </div>

        {/* Quick Actions Card */}
        <div className="card" style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div className="card-header" style={{ marginBottom: 12 }}>
              <div>
                <div className="card-title">Quick Actions</div>
                <div className="card-subtitle">
                  Operational shortcuts for {selectedBusiness?.name ?? 'your business'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <Link to="/businesses/new" className="btn btn-secondary btn-sm">
                + Add Business
              </Link>
              {selectedBusinessId && (
                <>
                  <Link to={`/businesses/${selectedBusinessId}/seo`} className="btn btn-secondary btn-sm">
                    🌐 Website Audit
                  </Link>
                  <Link to={`/businesses/${selectedBusinessId}/intelligence`} className="btn btn-secondary btn-sm">
                    💡 SEO Intelligence
                  </Link>
                  <Link to={`/businesses/${selectedBusinessId}/integrations`} className="btn btn-secondary btn-sm">
                    🔗 Google Integrations
                  </Link>
                </>
              )}
              <Link to="/settings" className="btn btn-secondary btn-sm">
                ⚙ Account Settings
              </Link>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              paddingTop: 12,
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.8rem',
              color: '#64748b',
            }}
          >
            <span>
              Target Website: <strong>{selectedBusiness?.website || 'None configured'}</strong>
            </span>
            {selectedBusinessId && (
              <Link
                to={`/businesses/${selectedBusinessId}/onboarding`}
                style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}
              >
                Edit Business Profile →
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* SECTION: 6 Key Real Metrics Grid */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Search & Technical SEO Telemetry</span>
          {dashboardLoading && (
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#64748b' }}>Updating...</span>
          )}
        </h2>

        <div className="metrics-grid">
          {/* Card 1: Search Console Impressions */}
          <div
            className="metric-card"
            style={{ '--metric-accent': '#8b5cf6', '--metric-icon-bg': '#f5f3ff' } as React.CSSProperties}
          >
            <div className="metric-label">Search Impressions</div>
            <div className="metric-value">
              {gscSummary ? (
                gscSummary.totalImpressions.toLocaleString()
              ) : googleStatus?.connected ? (
                '0'
              ) : (
                <span style={{ fontSize: '1.15rem', color: '#64748b' }}>Disconnected</span>
              )}
            </div>
            <div className="metric-change neutral">
              {gscSummary ? (
                <div>
                  <span>Source: Google Search Console</span>
                  <Link
                    to={selectedBusinessId ? `/businesses/${selectedBusinessId}/keywords` : '/keywords'}
                    style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                  >
                    Keywords →
                  </Link>
                </div>
              ) : googleStatus?.connected ? (
                <div>
                  <span>GSC property synced</span>
                  <Link
                    to={selectedBusinessId ? `/businesses/${selectedBusinessId}/keywords` : '/keywords'}
                    style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                  >
                    View Keywords →
                  </Link>
                </div>
              ) : (
                <Link
                  to={selectedBusinessId ? `/businesses/${selectedBusinessId}/integrations` : '/businesses'}
                  style={{ color: '#2563eb', textDecoration: 'none' }}
                >
                  Connect Search Console →
                </Link>
              )}
            </div>
            <div className="metric-icon">🔍</div>
          </div>

          {/* Card 2: Organic Search Clicks */}
          <div
            className="metric-card"
            style={{ '--metric-accent': '#10b981', '--metric-icon-bg': '#f0fdf4' } as React.CSSProperties}
          >
            <div className="metric-label">Organic Search Clicks</div>
            <div className="metric-value">
              {gscSummary ? (
                gscSummary.totalClicks.toLocaleString()
              ) : googleStatus?.connected ? (
                '0'
              ) : (
                <span style={{ fontSize: '1.15rem', color: '#64748b' }}>Disconnected</span>
              )}
            </div>
            <div className="metric-change neutral">
              {gscSummary ? (
                <div>
                  <span>Avg CTR: {((gscSummary.averageCtr ?? 0) * 100).toFixed(1)}%</span>
                  <Link
                    to={selectedBusinessId ? `/businesses/${selectedBusinessId}/keywords` : '/keywords'}
                    style={{ color: '#059669', textDecoration: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                  >
                    View Keywords →
                  </Link>
                </div>
              ) : (
                <>Google Search performance</>
              )}
            </div>
            <div className="metric-icon">🖱</div>
          </div>

          {/* Card 3: Google Maps Views (GBP) */}
          <div
            className="metric-card"
            style={{ '--metric-accent': '#3b82f6', '--metric-icon-bg': '#eff6ff' } as React.CSSProperties}
          >
            <div className="metric-label">Maps Impressions</div>
            <div className="metric-value">
              {gbpSummary ? (
                gbpSummary.mapsImpressions.toLocaleString()
              ) : googleStatus?.connected ? (
                '0'
              ) : (
                <span style={{ fontSize: '1.15rem', color: '#64748b' }}>Disconnected</span>
              )}
            </div>
            <div className="metric-change neutral">
              {gbpSummary ? (
                <>Total GBP Views: {gbpSummary.totalImpressions.toLocaleString()}</>
              ) : googleStatus?.connected ? (
                <>Profile linked</>
              ) : (
                <Link
                  to={selectedBusinessId ? `/businesses/${selectedBusinessId}/integrations` : '/businesses'}
                  style={{ color: '#2563eb', textDecoration: 'none' }}
                >
                  Connect Business Profile →
                </Link>
              )}
            </div>
            <div className="metric-icon">📍</div>
          </div>

          {/* Card 4: Customer Calls (GBP) */}
          <div
            className="metric-card"
            style={{ '--metric-accent': '#f59e0b', '--metric-icon-bg': '#fffbeb' } as React.CSSProperties}
          >
            <div className="metric-label">Direct Phone Calls</div>
            <div className="metric-value">
              {gbpSummary ? (
                gbpSummary.callClicks.toLocaleString()
              ) : googleStatus?.connected ? (
                '0'
              ) : (
                <span style={{ fontSize: '1.15rem', color: '#64748b' }}>Disconnected</span>
              )}
            </div>
            <div className="metric-change neutral">
              {gbpSummary ? (
                <>Directions: {gbpSummary.directionRequests.toLocaleString()}</>
              ) : (
                <>Call clicks from Maps listing</>
              )}
            </div>
            <div className="metric-icon">📞</div>
          </div>

          {/* Card 5: Open SEO Opportunities */}
          <div
            className="metric-card"
            style={{ '--metric-accent': '#06b6d4', '--metric-icon-bg': '#ecfeff' } as React.CSSProperties}
          >
            <div className="metric-label">Open Opportunities</div>
            <div className="metric-value">
              {intelligenceSummary ? intelligenceSummary.openOpportunities : 0}
            </div>
            <div className="metric-change neutral">
              {intelligenceSummary ? (
                <>
                  {intelligenceSummary.criticalCount} Critical · {intelligenceSummary.highCount} High
                </>
              ) : (
                <Link
                  to={selectedBusinessId ? `/businesses/${selectedBusinessId}/intelligence` : '/businesses'}
                  style={{ color: '#2563eb', textDecoration: 'none' }}
                >
                  Run Intelligence Engine →
                </Link>
              )}
            </div>
            <div className="metric-icon">💡</div>
          </div>

          {/* Card 6: Website Audit Health */}
          <div
            className="metric-card"
            style={{ '--metric-accent': '#ec4899', '--metric-icon-bg': '#fdf2f8' } as React.CSSProperties}
          >
            <div className="metric-label">Website Health</div>
            <div className="metric-value">
              {latestAudit ? (
                latestAudit.overallScore !== null ? (
                  `${latestAudit.overallScore}%`
                ) : latestAudit.technicalScore !== null ? (
                  `${Math.round((latestAudit.technicalScore / 25) * 100)}%`
                ) : (
                  <span style={{ fontSize: '1.2rem' }}>{latestAudit.status}</span>
                )
              ) : (
                <span style={{ fontSize: '1.15rem', color: '#64748b' }}>No Audit</span>
              )}
            </div>
            <div className="metric-change neutral">
              {latestAudit ? (
                <>
                  {latestAudit.pagesCrawled} pages crawled · {auditErrorsCount} errors
                </>
              ) : (
                <Link
                  to={selectedBusinessId ? `/businesses/${selectedBusinessId}/seo` : '/businesses'}
                  style={{ color: '#2563eb', textDecoration: 'none' }}
                >
                  Run Website Audit →
                </Link>
              )}
            </div>
            <div className="metric-icon">🌐</div>
          </div>
        </div>
      </div>

      {/* SECTION: Operational Automation Health (Phase 6 Step 9 Real Data) */}
      <div
        className="card"
        style={{
          padding: 24,
          borderRadius: 12,
          marginBottom: 28,
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
              Automation Operations Health
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              Real-time monitoring of backend audit crawls, Google syncs, and AI intelligence workflows
            </p>
          </div>

          <span
            className={`badge ${
              automationSummary && automationSummary.failedToday > 0 ? 'badge-amber' : 'badge-green'
            }`}
            style={{ padding: '4px 10px', fontSize: '0.8rem' }}
          >
            {automationSummary && automationSummary.failedToday > 0
              ? `⚠ ${automationSummary.failedToday} Failed Today`
              : '✓ All Systems Operational'}
          </span>
        </div>

        {/* 4 Health Stat Boxes */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 14,
            marginBottom: 20,
          }}
        >
          <div style={{ padding: '14px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase' }}>
              Completed Today
            </span>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#15803d', marginTop: 4 }}>
              {automationSummary ? automationSummary.completedToday : 0}
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: automationSummary && automationSummary.failedToday > 0 ? '#fef2f2' : '#f8fafc',
              borderRadius: 8,
              border: `1px solid ${automationSummary && automationSummary.failedToday > 0 ? '#fecaca' : '#e2e8f0'}`,
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: automationSummary && automationSummary.failedToday > 0 ? '#991b1b' : '#64748b',
                textTransform: 'uppercase',
              }}
            >
              Failed Today
            </span>
            <div
              style={{
                fontSize: '1.6rem',
                fontWeight: 800,
                color: automationSummary && automationSummary.failedToday > 0 ? '#dc2626' : '#64748b',
                marginTop: 4,
              }}
            >
              {automationSummary ? automationSummary.failedToday : 0}
            </div>
          </div>

          <div style={{ padding: '14px 16px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase' }}>
              Currently Running
            </span>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#2563eb', marginTop: 4 }}>
              {automationSummary ? automationSummary.running : 0}
            </div>
          </div>

          <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
              Queued / Stale
            </span>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
              {automationSummary ? automationSummary.queued + automationSummary.staleRunning : 0}
            </div>
          </div>
        </div>

        {/* Recent Automation Executions Table */}
        <div>
          <h4 style={{ margin: '0 0 12px', fontSize: '0.92rem', fontWeight: 700, color: '#334155' }}>
            Recent Automation Executions
          </h4>

          {recentExecutions.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                background: '#f8fafc',
                borderRadius: 8,
                color: '#64748b',
                fontSize: '0.88rem',
              }}
            >
              No automation executions recorded yet for this business.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#64748b' }}>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Workflow</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Trigger</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Executed</th>
                  </tr>
                </thead>
                <tbody>
                  {recentExecutions.map((exec) => (
                    <tr key={exec.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a' }}>
                        {formatWorkflowName(exec.workflowKey)}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>
                        {formatTriggerType(exec.triggerType)}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span
                          className={`badge ${
                            exec.status === 'COMPLETED'
                              ? 'badge-green'
                              : exec.status === 'RUNNING'
                              ? 'badge-blue'
                              : exec.status === 'FAILED'
                              ? 'badge-red'
                              : 'badge-amber'
                          }`}
                          style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                        >
                          {exec.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>
                        {formatRelativeTime(exec.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* SECTION: Planned Future Modules (Explicitly labeled) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
        {[
          { title: 'Keyword Rankings', desc: 'Track local keyword rank positions over time in the Maps 3-Pack', icon: '📈' },
          { title: 'Competitor Monitor', desc: 'Compare your search visibility against local direct competitors', icon: '🏆' },
          { title: 'Review Manager', desc: 'Read and draft AI-assisted responses to customer reviews', icon: '⭐' },
          { title: 'AI Assistant', desc: 'Custom conversational agent for local SEO strategy recommendations', icon: '🤖' },
        ].map((item) => (
          <div key={item.title} className="card">
            <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>{item.icon}</div>
            <div className="card-title" style={{ marginBottom: 6, fontSize: '1rem', fontWeight: 700 }}>
              {item.title}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{item.desc}</div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
